import { Address, CustomerWalletStatusType } from 'types/customers'
import {
  CryptoSendAndReceiveOptions,
  CustomerModels,
  FiatPayinAndPayoutOptions,
  GetCryptoSendAndReceiveOptionsProps,
  GetCustomerAddressOptions,
  GetFiatPayinAndPayoutOptionsProps,
  UseCustomerWalletActionsProps,
  WalletActionOptions,
} from './types'

export const getCustomerAddress = (address: Address, options?: GetCustomerAddressOptions) => {
  const addressLines = [address?.addressLine1, address?.addressLine2, address?.city]
  if (options?.useFullAddress) {
    addressLines?.push(address?.postalCode, address?.stateCode, address?.countryCode)
  } else if (options?.useAddressExcludingCountry) {
    addressLines?.push(address?.postalCode, address?.stateCode)
  } else if (options?.usePostalCode) {
    addressLines?.push(address?.postalCode)
  } else {
    addressLines?.push(address?.country)
  }
  return addressLines?.filter(Boolean)?.join(', ')
}

const getCryptoSendAndReceiveOptions = ({
  customerWallet,
  isEmbeddedCustomer,
  isViewOnlyRole,
  isActive,
  isEpcWalletPayoutsCapabilityEnabled,
  isEpcWalletPayinsCapabilityEnabled,
}: GetCryptoSendAndReceiveOptionsProps): CryptoSendAndReceiveOptions => {
  const isCryptoSendEnabled = customerWallet?.balance?.value > 0 && isEmbeddedCustomer
  const isCryptoReceiveEnabled = isEmbeddedCustomer
  const isActiveAndNotViewOnlyRole = !isViewOnlyRole && isActive
  const isCryptoSendsEnabled = !isViewOnlyRole && isActive && isCryptoSendEnabled
  const isCryptoSendOptionEnabled = isEmbeddedCustomer
    ? isCryptoSendsEnabled && isEpcWalletPayoutsCapabilityEnabled
    : isCryptoSendsEnabled
  const isCryptoReceiveOptionEnabled = isEmbeddedCustomer
    ? isActiveAndNotViewOnlyRole && isCryptoReceiveEnabled && isEpcWalletPayinsCapabilityEnabled
    : isActiveAndNotViewOnlyRole && isCryptoReceiveEnabled

  return {
    isCryptoSendOptionEnabled,
    isCryptoReceiveOptionEnabled,
  }
}

const getFiatPayinAndPayoutOptions = ({
  isEmbeddedCustomer,
  isViewOnlyRole,
  supportsThirdParty,
  isActive,
  isEpcWalletPayoutsCapabilityEnabled,
  isEpcWalletPayinsCapabilityEnabled,
  supportsInternalTransfers,
  supportsDeposits,
  isEpcWalletInternalTransfersCapabilityEnabled,
}: GetFiatPayinAndPayoutOptionsProps): FiatPayinAndPayoutOptions => {
  const isActiveAndNotViewOnlyRole = !isViewOnlyRole && isActive

  // Unified payout
  const isPayoutEnabled = isActiveAndNotViewOnlyRole && supportsThirdParty
  const isUnifiedPayoutEnabled = isEmbeddedCustomer
    ? isPayoutEnabled && isEpcWalletPayoutsCapabilityEnabled
    : isPayoutEnabled
  const isUnifiedPayoutOptionEnabled = isEmbeddedCustomer
    ? isUnifiedPayoutEnabled && isEpcWalletPayoutsCapabilityEnabled
    : isUnifiedPayoutEnabled

  // Internal transfers
  const isMoveEnabled = supportsInternalTransfers && isActiveAndNotViewOnlyRole
  const isMoveOptionEnabled = isEmbeddedCustomer
    ? isMoveEnabled && isEpcWalletInternalTransfersCapabilityEnabled
    : isMoveEnabled

  // Fiat deposits
  const isFiatDepositEnabled = isActiveAndNotViewOnlyRole && supportsDeposits
  const isFiatDepositOptionEnabled = isEmbeddedCustomer
    ? isFiatDepositEnabled && isEpcWalletPayinsCapabilityEnabled
    : isFiatDepositEnabled

  return {
    isUnifiedPayoutOptionEnabled,
    isMoveOptionEnabled,
    isFiatDepositOptionEnabled,
  }
}

export const getCustomerWalletActions = ({
  accountCapabilities,
  customerWallet,
  isViewOnlyRole,
  customerModel,
}: UseCustomerWalletActionsProps): WalletActionOptions => {
  const isEmbeddedCustomer = customerModel === CustomerModels.EMBEDDED

  const {
    isEpcWalletPayoutsCapabilityEnabled,
    isEpcWalletPayinsCapabilityEnabled,
    isEpcWalletInternalTransfersCapabilityEnabled,
  } = accountCapabilities || {}

  const { supportsInternalTransfers, supportsDeposits, supportsThirdParty, status } =
    customerWallet || {}
  const isActive = status === CustomerWalletStatusType.ACTIVE

  // Crypto sends and receives
  const { isCryptoSendOptionEnabled, isCryptoReceiveOptionEnabled } =
    getCryptoSendAndReceiveOptions({
      customerWallet,
      isEmbeddedCustomer,
      isViewOnlyRole,
      isActive,
      isEpcWalletPayoutsCapabilityEnabled,
      isEpcWalletPayinsCapabilityEnabled,
    })

  // Fiat payins, payouts and internal transfers
  const { isUnifiedPayoutOptionEnabled, isMoveOptionEnabled, isFiatDepositOptionEnabled } =
    getFiatPayinAndPayoutOptions({
      isEmbeddedCustomer,
      isViewOnlyRole,
      supportsThirdParty,
      isActive,
      isEpcWalletPayoutsCapabilityEnabled,
      isEpcWalletPayinsCapabilityEnabled,
      supportsInternalTransfers,
      supportsDeposits,
      isEpcWalletInternalTransfersCapabilityEnabled,
    })

  // Transaction export
  const isTransactionExportOptionEnabled = !isViewOnlyRole && isActive

  return {
    isCryptoSendOptionEnabled,
    isCryptoReceiveOptionEnabled,

    isUnifiedPayoutOptionEnabled,
    isMoveOptionEnabled,
    isFiatDepositOptionEnabled,
    isTransactionExportOptionEnabled,
  }
}
